// src/cockpit4/components/WorkspaceAccessGate.tsx
// ============================================
// WorkspaceAccessGate - 财务/设置加密锁（v6.39 游客全览）
//
// 用途：游客点击「财务中心 / 设置」时，不渲染真实内容，而是显示加密锁页面
//       （入口可见、内容加密）。使用不透明背景完全盖住下层，避免敏感数据透过。
//       通过内嵌登录（LoginModal）解锁——登录成功后角色变化，自动进入真实内容。
//
// 安全：展示层加密 + 后端 server.py 游客只读硬边界（写/LLM 403）。
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, KeyRound, Wallet, Settings as SettingsIcon, LogIn, AlertTriangle } from "lucide-react";
import type { WorkspaceId } from "../store";
import LoginModal from "./LoginModal";

// 模块信息映射
const MODULE_INFO: Record<string, { name: string; desc: string; icon: React.ComponentType<{ size?: number | string; className?: string; style?: React.CSSProperties }>; color: string }> = {
  finance: {
    name: "财务中心",
    desc: "收入 / 利润 / 现金流 / 成本 / ROI 等核心经营数据",
    icon: Wallet,
    color: "#10B981",
  },
  settings: {
    name: "设置",
    desc: "API 密钥 / 权限 / 审计日志 / 安全加固等系统配置",
    icon: SettingsIcon,
    color: "#9CA3AF",
  },
};

export default function WorkspaceAccessGate({ workspaceId }: { workspaceId: WorkspaceId }) {
  const [loginOpen, setLoginOpen] = useState(false);
  const info = MODULE_INFO[workspaceId] || MODULE_INFO.settings;
  const Icon = info.icon;

  return (
    <>
      {/* 不透明背景：完全盖住下层真实内容，防止敏感数据透过 */}
      <div className="absolute inset-0 z-40 flex items-center justify-center overflow-hidden bg-[#0B1220]">
        {/* 背景装饰：网格 + 扫描线 + 角光晕 */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(59,130,246,0.6) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(59,130,246,0.6) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="relative flex flex-col items-center gap-5 px-10 py-10 rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-2xl shadow-2xl max-w-sm w-full mx-6"
        >
          {/* 锁图标 */}
          <div className="relative">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: `${info.color}15`, border: `1px solid ${info.color}35` }}
            >
              <Icon size={28} style={{ color: info.color }} />
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0B1220] border border-amber-500/40 flex items-center justify-center">
              <Lock size={11} className="text-amber-400" />
            </div>
          </div>

          {/* 标题 */}
          <div className="text-center space-y-1.5">
            <p className="text-[15px] font-semibold text-white tracking-tight">{info.name}</p>
            <p className="text-[11px] text-gray-400 leading-relaxed">{info.desc}</p>
          </div>

          {/* 加密标识 */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/[0.06] border border-amber-500/20">
            <KeyRound size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-amber-300 uppercase tracking-widest">
              已加密 · 需授权访问
            </span>
          </div>

          {/* 解锁按钮 */}
          <button
            onClick={() => setLoginOpen(true)}
            className="flex items-center gap-1.5 px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-emerald-500 text-white text-[13px] font-medium hover:from-blue-400 hover:to-emerald-400 transition-all shadow-lg shadow-blue-500/20"
          >
            <LogIn size={14} />
            输入密码解锁
          </button>

          {/* 安全提示 */}
          <div className="flex items-start gap-1.5 text-[9px] font-mono text-gray-600">
            <ShieldCheck size={10} className="mt-0.5 flex-shrink-0" />
            <span>该模块含敏感经营数据，仅授权账号可访问</span>
          </div>
          <div className="flex items-start gap-1.5 text-[9px] font-mono text-gray-700">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            <span>游客访问记录将被审计</span>
          </div>
        </motion.div>
      </div>

      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}
    </>
  );
}
